import React, { useState, useEffect } from 'react';
import { Upload, FolderPlus, Grid3x3, List, Search, Trash2, Eye, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import { usePagination } from '../../hooks/usePagination';
import TablePagination from '../../components/ui/table-pagination';

const FILES_PER_PAGE = 24; // 4 rows of the 6-column grid

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminMediaGallery = () => {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState('/');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const {
    currentItems: paginatedFiles,
    currentPage,
    totalPages,
    goToPage,
    totalItems,
    startIndex,
    endIndex,
  } = usePagination(files, FILES_PER_PAGE);

  // Helper to get full media URL (handles legacy /api/media/serve/ paths)
  const getMediaUrl = (relativePath) => resolveMediaUrl(relativePath);

  // Prefer the optimized "large" variant when the row was optimized at upload time.
  // This guarantees that copying a URL from the gallery also returns an optimized asset.
  const getDeliveryUrl = (file) => {
    if (!file) return '';
    const largeUrl = file?.optimized?.large?.jpg?.url;
    if (largeUrl) return getMediaUrl(largeUrl);
    return getMediaUrl(file.url);
  };

  useEffect(() => {
    fetchMedia();
  }, [currentFolder]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/media/files?folder=${encodeURIComponent(currentFolder)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(response.data.files);
      setFolders(response.data.folders);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch media files',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }


    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', currentFolder);


      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${API}/media/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });


      toast({
        title: 'Success',
        description: 'File uploaded successfully'
      });
      
      // Reset input
      e.target.value = '';
      
      fetchMedia();
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error response:', error.response?.data);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to upload file',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    const input = document.getElementById('file-upload');
    if (input) {
      input.click();
    } else {
      console.error('File input not found!');
    }
  };

  const handleCreateFolder = async () => {
    const name = prompt('Enter folder name:');
    if (!name) return;

    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('name', name);
      formData.append('parentPath', currentFolder);

      await axios.post(`${API}/media/folders`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: 'Success',
        description: 'Folder created successfully'
      });
      fetchMedia();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create folder',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API}/media/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: 'Success',
        description: 'File deleted successfully'
      });
      fetchMedia();
      setSelectedFile(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete file',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteFolder = async (folderId, folderName) => {
    if (!window.confirm(`Are you sure you want to delete the folder "${folderName}"? This folder must be empty.`)) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API}/media/folders/${folderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: 'Success',
        description: 'Folder deleted successfully'
      });
      fetchMedia();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete folder';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const navigateToFolder = (folderPath) => {
    setCurrentFolder(folderPath);
  };

  const navigateUp = () => {
    if (currentFolder === '/') return;
    const parts = currentFolder.split('/').filter(Boolean);
    parts.pop();
    setCurrentFolder('/' + parts.join('/'));
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    toast({
      title: 'Success',
      description: 'URL copied to clipboard'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Media Gallery</h1>
          <p className="text-sm text-gray-600 mt-1">Manage your images and files</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleCreateFolder}
            variant="outline"
            className="gap-2"
          >
            <FolderPlus className="h-4 w-4" />
            New Folder
          </Button>
          <Button
            onClick={handleUploadClick}
            className="bg-[#be1f1a] hover:bg-[#a71b17] gap-2"
            disabled={uploading}
          >
            <Upload className="h-4 w-4" />
            {uploading ? 'Uploading...' : 'Upload File'}
          </Button>
          <input
            id="file-upload"
            type="file"
            className="hidden"
            onChange={handleFileUpload}
            accept="image/*,video/*,application/pdf"
          />
        </div>
      </div>

      {/* Breadcrumb & Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => setCurrentFolder('/')}
              className="text-blue-600 hover:underline"
            >
              Home
            </button>
            {currentFolder.split('/').filter(Boolean).map((part, idx, arr) => (
              <React.Fragment key={idx}>
                <span>/</span>
                <button
                  onClick={() => {
                    const path = '/' + arr.slice(0, idx + 1).join('/');
                    setCurrentFolder(path);
                  }}
                  className="text-blue-600 hover:underline"
                >
                  {part}
                </button>
              </React.Fragment>
            ))}
            {currentFolder !== '/' && (
              <Button
                onClick={navigateUp}
                variant="ghost"
                size="sm"
                className="ml-2"
              >
                ← Back
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid3x3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading...</div>
        ) : (
          <>
            {/* Folders */}
            {folders.length > 0 && (
              <div className={viewMode === 'grid' ? 'grid grid-cols-6 gap-4 p-4' : 'divide-y'}>
                {folders.map((folder) => (
                  <div
                    key={folder.id}
                    className="relative group cursor-pointer p-4 hover:bg-gray-50 rounded-lg border-2 border-dashed"
                  >
                    <div onClick={() => navigateToFolder(folder.path)}>
                      <FolderPlus className="h-12 w-12 text-yellow-500 mx-auto mb-2" />
                      <p className="text-sm text-center font-medium truncate">{folder.name}</p>
                    </div>
                    {/* Delete button - shows on hover */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder.id, folder.name);
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete folder"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Files */}
            {files.length > 0 ? (
              <div className={viewMode === 'grid' ? 'grid grid-cols-6 gap-4 p-4' : 'divide-y'}>
                {paginatedFiles.map((file) => (
                  <div
                    key={file.id}
                    onClick={() => setSelectedFile(file)}
                    className={`cursor-pointer hover:bg-gray-50 ${
                      viewMode === 'grid' ? 'rounded-lg border p-2' : 'p-4 flex items-center gap-4'
                    }`}
                  >
                    {file.mimeType.startsWith('image/') ? (
                      <img
                        src={getMediaUrl(file.url)}
                        alt={file.originalName}
                        className={viewMode === 'grid' ? 'w-full h-32 object-cover rounded' : 'w-16 h-16 object-cover rounded'}
                      />
                    ) : (
                      <div className={`${viewMode === 'grid' ? 'h-32' : 'w-16 h-16'} bg-gray-200 flex items-center justify-center rounded`}>
                        <ImageIcon className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                    <div className={viewMode === 'list' ? 'flex-1' : 'mt-2'}>
                      <p className="text-sm font-medium truncate">{file.originalName}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : folders.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <ImageIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>No files or folders here</p>
                <p className="text-sm">Upload your first file to get started</p>
              </div>
            ) : null}

            {totalPages > 1 && (
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                startIndex={startIndex}
                endIndex={endIndex}
                onPageChange={goToPage}
              />
            )}
          </>
        )}
      </div>

      {/* File Preview Modal */}
      {selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold">{selectedFile.originalName}</h2>
                <button onClick={() => setSelectedFile(null)}>
                  <X className="h-6 w-6" />
                </button>
              </div>

              {selectedFile.mimeType.startsWith('image/') && (
                <img
                  src={getMediaUrl(selectedFile.url)}
                  alt={selectedFile.originalName}
                  className="w-full rounded-lg mb-4"
                />
              )}

              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">URL:</label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="text"
                      value={getDeliveryUrl(selectedFile)}
                      readOnly
                      className="flex-1 px-3 py-2 border rounded text-sm"
                    />
                    <Button onClick={() => copyToClipboard(getDeliveryUrl(selectedFile))}>
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Type:</span> {selectedFile.mimeType}
                  </div>
                  <div>
                    <span className="font-medium">Size:</span> {formatFileSize(selectedFile.size)}
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    onClick={() => handleDeleteFile(selectedFile.id)}
                    variant="destructive"
                    className="gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete File
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMediaGallery;
